import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/base/Breadcrumb";
import RankSidebar from "@/components/feature/RankSidebar";
import RankRow from "@/components/feature/RankRow";
import { topicList } from "@/mocks/topics";
import type { Topic } from "@/types/content";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topics: Topic[] = topicList;
  const sorted = [...topics].sort((a, b) => a.rank - b.rank);

  const topic = topics.find((t) => t.id === id);

  if (!topic) {
    return (
      <SiteLayout>
        <Seo
          title="爆料不存在 - 瓜点"
          description="你访问的爆料内容不存在或已被移除，可返回首页查看今日网络爆料热门话题与热议榜单。"
          keywords="网络爆料,爆料不存在"
          path="/topic"
        />
        <div className="mx-auto flex w-full max-w-[800px] flex-col items-center px-4 py-20 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-2xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            这条爆料找不到啦
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            内容可能已被移除，或者链接输入有误。去看看今天大家都在讨论什么吧。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回首页
            </Link>
            <Link
              to="/rank"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看热议榜单
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  const related = topics
    .filter((t) => t.id !== topic.id && t.categorySlug === topic.categorySlug)
    .slice(0, 3);
  const fallbackRelated = topics.filter((t) => t.id !== topic.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallbackRelated;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      articleSection: topic.category,
      keywords: topic.tags.join(","),
      datePublished: topic.publishDate,
      inLanguage: "zh-CN",
      author: { "@type": "Organization", name: "瓜点 · 网络爆料雷达" },
      publisher: { "@type": "Organization", name: "瓜点 · 网络爆料雷达" },
      mainEntityOfPage: `https://sunshinelabs.cn/topic/${topic.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sunshinelabs.cn/" },
        { "@type": "ListItem", position: 2, name: "热议榜单", item: "https://sunshinelabs.cn/rank" },
        {
          "@type": "ListItem",
          position: 3,
          name: topic.category,
          item: `https://sunshinelabs.cn/rank?category=${topic.categorySlug}`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: topic.title,
          item: `https://sunshinelabs.cn/topic/${topic.id}`,
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title={`${topic.title} - 网络爆料 | 瓜点`}
        description={`${topic.summary.slice(0, 70)}…了解《${topic.title}》的事件经过、关键信息与多方说法。`}
        keywords={`网络爆料,${topic.category},${topic.tags.join(",")}`}
        path={`/topic/${topic.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />

      <div className="w-full bg-background-100">
        <div className="mx-auto w-full max-w-[1240px] px-4 py-6 md:px-6">
          <Breadcrumb
            items={[
              { label: "热议榜单", to: "/rank" },
              { label: topic.category, to: `/rank?category=${topic.categorySlug}` },
              { label: "爆料详情" },
            ]}
          />
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1240px] grid-cols-1 gap-10 px-4 py-8 md:px-6 md:py-12 lg:grid-cols-12">
        <article className="lg:col-span-8">
          <header>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Link
                to={`/rank?category=${topic.categorySlug}`}
                className="rounded-full bg-primary-100 px-2.5 py-1 text-primary-700 transition-colors hover:bg-primary-200"
              >
                {topic.category}
              </Link>
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-1 text-accent-900">
                <i className="ri-fire-fill"></i>
                热度 {topic.heat.toLocaleString("zh-CN")}
              </span>
              <span className="text-foreground-500">更新于 {topic.updatedTime}</span>
            </div>

            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>

            <p className="mt-4 border-l-2 border-primary-400 pl-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {topic.summary}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                首发 {topic.publishTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-book-open-line"></i>
                约 {topic.readMinutes} 分钟读完
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line"></i>
                {topic.comments} 条讨论
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line"></i>
                {topic.source}
              </span>
            </div>

            <div className="mt-6 h-[240px] w-full overflow-hidden rounded-lg md:h-[380px]">
              <img
                src={topic.cover}
                alt={`${topic.title} 相关配图`}
                title={`${topic.title} - 网络爆料热议事件`}
                className="h-full w-full object-cover"
              />
            </div>
          </header>

          <section className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5 md:p-6">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-accent-950">
              <i className="ri-list-check-2"></i>
              关键信息
            </h2>
            <ul className="mt-4 space-y-2.5">
              {topic.keyPoints.map((point) => (
                <li key={point} className="flex gap-3 text-sm leading-relaxed text-accent-900">
                  <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600"></i>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </section>

          <div className="mt-8 flex flex-col gap-8">
            {topic.sections.map((section, idx) => (
              <section key={section.heading}>
                <h2 className="flex items-baseline gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  <span className="font-label text-sm text-primary-500">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  {section.heading}
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {section.paragraphs.map((p) => (
                    <p key={p} className="text-sm leading-loose text-foreground-700 md:text-base">
                      {p}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-xs text-foreground-500">相关标签</span>
            {topic.tags.map((tag) => (
              <Link
                key={tag}
                to={`/rank?q=${encodeURIComponent(tag)}`}
                className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关爆料推荐</h2>
            <ul className="mt-4 divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 px-2">
              {relatedList.map((t, idx) => (
                <li key={t.id}>
                  <RankRow topic={t} position={idx + 1} />
                </li>
              ))}
            </ul>
          </section>
        </article>

        <div className="flex flex-col gap-6 lg:col-span-4">
          <RankSidebar topics={sorted.slice(0, 6)} />

          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-links-line text-secondary-600"></i>
              继续吃瓜
            </h2>
            <div className="mt-3 flex flex-col gap-2 text-sm">
              <Link
                to={`/rank?category=${topic.categorySlug}`}
                className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-foreground-800 transition-colors hover:text-primary-600"
              >
                更多「{topic.category}」爆料
                <i className="ri-arrow-right-s-line"></i>
              </Link>
              <Link
                to="/rank"
                className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-foreground-800 transition-colors hover:text-primary-600"
              >
                查看网络爆料热议榜单
                <i className="ri-arrow-right-s-line"></i>
              </Link>
              <Link
                to="/about"
                className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2.5 text-foreground-800 transition-colors hover:text-primary-600"
              >
                了解我们的内容原则
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}